import { toZonedTime } from 'date-fns-tz';

import { WorkCalendarOrder, WorkCalendarRecord } from '../types';

export interface JobWithDateRange {
    order: WorkCalendarOrder;
    dateRange: {
        start: Date;
        end: Date;
    };
}

interface GetJobsForSelectedDatesParams {
    selectedDates: WorkCalendarRecord[];
    timeZone: string;
}

/**
 * Extracts and deduplicates jobs from selected dates.
 * Jobs spanning multiple selected dates are shown only once.
 * Results are sorted by start date (earliest first).
 */
export function getJobsForSelectedDates({
    selectedDates,
    timeZone
}: GetJobsForSelectedDatesParams): JobWithDateRange[] {
    const jobsMap = new Map<string, JobWithDateRange>();

    for (const selectedDate of selectedDates) {
        for (const order of selectedDate.orders) {
            if (jobsMap.has(order.id)) {
                continue;
            }

            if (order.shifts.length === 0) {
                continue;
            }

            const shiftDates = order.shifts.map(shift => ({
                start: toZonedTime(shift.dateTimeStart, timeZone),
                end: toZonedTime(shift.dateTimeEnd, timeZone)
            }));

            const minDate = new Date(Math.min(...shiftDates.map(d => d.start.getTime())));
            const maxDate = new Date(Math.max(...shiftDates.map(d => d.end.getTime())));

            jobsMap.set(order.id, {
                order,
                dateRange: {
                    start: minDate,
                    end: maxDate
                }
            });
        }
    }

    const jobs = Array.from(jobsMap.values());
    jobs.sort((a, b) => a.dateRange.start.getTime() - b.dateRange.start.getTime());

    return jobs;
}
